import "./Events.css";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDMY } from "../../utils/date";
import { useDispatch, useSelector } from "react-redux";
import { GetEvents } from "../../apis/api/EventsApi";

const scaleIn = { hidden:{opacity:0,y:20}, show:{opacity:1,y:0} };
const stagger = { show:{ transition:{ staggerChildren:.08 } } };

const EventCards = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const events   = location?.state?.Event || [];
  const fromPath = location?.state?.from || "/EventsMenu";
  const categoryKey = location?.state?.categoryKey || null;

  const dispatch = useDispatch();
  const { events: storeEvents, loading } = useSelector((s) => s.EventSlice);

  const go = (event) =>
    navigate("/Events", { state: { Events: event, from: location.pathname } });

  useEffect(() => {
    if (!Array.isArray(storeEvents) || storeEvents.length === 0) {
      dispatch(GetEvents());
    }
  }, [dispatch]);

  const now = Date.now() / 1000;

  const eventsToShow = (() => {
    const list = Array.isArray(events) && events.length > 0
      ? events
      : Array.isArray(storeEvents) ? storeEvents : [];

    if (categoryKey === "current") return list.filter(e => Number(e.end_date) >= now);
    if (categoryKey === "past")    return list.filter(e => Number(e.end_date) <  now);
    return list;
  })();

  return (
    <motion.div className="ec-wrap"
      initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
      transition={{ duration:.4, ease:[.22,.68,0,1.2] }}>

      {eventsToShow.length > 0 ? (
        <motion.div
          className={`ec-grid ${eventsToShow.length === 1 ? "ec-grid-single" : ""}`}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          {eventsToShow.map((event) => (
            <motion.div key={event.id} className="ec-card"
              variants={scaleIn}
              transition={{ duration:.35, ease:[.22,.68,0,1.2] }}
              whileHover={{ y:-5, boxShadow:"0 16px 40px rgba(88,106,217,.14)" }}
              onClick={() => go(event)}>

              <div className="ec-img-wrap">
                <img src={event.file_url?.replace("/thumb","")}
                  className="ec-img" alt={event.Event_Name}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
                <div className="ec-img-fallback" style={{ display: "none" }}>
                  <i className="bx bx-image-alt"></i>
                  <span>No preview available</span>
                </div>
                <span className={`ec-status ${Number(event.end_date) >= Date.now()/1000 ? "current" : "past"}`}>
                  {Number(event.end_date) >= Date.now()/1000 ? "Live" : "Past"}
                </span>
              </div>

              <div className="ec-body">
                <h3 className="ec-name">{event.Event_Name}</h3>
                <div className="ec-meta">
                  {event.Location && (
                    <span className="ec-meta-row">
                      <i className="bx bx-map-pin"></i>
                      {event.Location?.split(":")[1] || event.Location}
                    </span>
                  )}
                  {event.start_date && (
                    <span className="ec-meta-row">
                      <i className="bx bx-calendar"></i>
                      {formatDMY(event.start_date)}
                      {event.end_date && ` → ${formatDMY(event.end_date)}`}
                    </span>
                  )}
                  {event.timeZone && (
                    <span className="ec-meta-row">
                      <i className="bx bx-time"></i>
                      {event.timeZone}
                    </span>
                  )}
                </div>
                {event.description && (
                  <p className="ec-desc">
                    {event.description}
                  </p>
                )}
                <button className="ec-btn">
                  <i className="bx bx-info-circle"></i> View Details
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : loading ? (
        <div className="ec-empty">
          <i className="bx bx-loader-alt bx-spin"></i>
          <p>Loading events...</p>
        </div>
      ) : (
        <motion.div
          className="ec-empty"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.22, 0.68, 0, 1.2] }}
        >
          <div className="ec-empty-icon-wrap">
            <i className="bx bx-calendar-x"></i>
          </div>

          <h3 className="ec-empty-title">
            {categoryKey === "current"
              ? "No Live Events Available"
              : categoryKey === "past"
              ? "No Past Events Found"
              : "No Events Found"}
          </h3>

          <p className="ec-empty-desc">
            {categoryKey === "current"
              ? "There are currently no active or upcoming live events scheduled. Check back soon for new announcements."
              : categoryKey === "past"
              ? "There are no past event records available in this section at the moment."
              : "There are no events listed in this category right now. Check back later."}
          </p>

          <div className="ec-empty-actions">
            <motion.button
              className="ec-empty-btn-primary"
              onClick={() => navigate("/EventsMenu")}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              type="button"
            >
              <i className="bx bx-arrow-back"></i> Back to Events Menu
            </motion.button>
          </div>

          <div className="ec-empty-tips">
            <span className="ec-tip-item">
              <i className="bx bx-globe"></i> Global Conferences
            </span>
            <span className="ec-tip-item">
              <i className="bx bx-group"></i> Member Meetings
            </span>
            <span className="ec-tip-item">
              <i className="bx bx-badge-check"></i> Verified Network Events
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EventCards;
